import _extends from "@babel/runtime/helpers/extends";
import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import deepEqual from 'deep-equal';
import { useDialogExpansionContext } from '../dialog-expansion-context';
import { INITIAL_STATE, reducer } from './product-router-state';

/**
 * ProductRouter context is used for controlling visibility and access of products for the user.
 *
 * @function showProduct - Triggered when the user clicks on the UI element (eg: Tab) to enable the product.
 */

const ProductContext = /*#__PURE__*/createContext({
  getProduct: id => undefined,
  getActiveProduct: () => undefined,
  getPrimaryProduct: () => undefined,
  addProduct: () => undefined,
  updateProduct: () => undefined,
  removeProduct: () => undefined,
  showProduct: () => undefined,
  products: []
});
export const ProductRouter = ({
  __initialProducts = INITIAL_STATE,
  children
}) => {
  const [products, dispatch] = useReducer(reducer, __initialProducts);
  const {
    isExpanded
  } = useDialogExpansionContext();
  useEffect(() => {
    if (!isExpanded) {
      dispatch({
        type: 'HIDE_ALL'
      });
    }
  }, [isExpanded]);
  const addProduct = useCallback(payload => {
    return dispatch({
      type: 'ADD',
      payload
    });
  }, [dispatch]);
  const updateProduct = useCallback(payload => {
    return dispatch({
      type: 'UPDATE',
      payload
    });
  }, [dispatch]);
  const removeProduct = useCallback(id => {
    return dispatch({
      type: 'REMOVE',
      payload: {
        id
      }
    });
  }, [dispatch]);

  /**
   * @function showProduct will ensure that only one product is displayed at a time
   * @param {string} id - The product id which has to be made visible.
   */
  const showProduct = useCallback(id => dispatch({
    type: 'SHOW',
    payload: {
      id
    }
  }), [dispatch]);
  const getActiveProduct = useCallback(() => {
    return products.find(product => product.isDisplayed);
  }, [products]);
  const productsRef = useRef({});
  const getProduct = useCallback(id => {
    const product = products.find(product => product.id === id);
    const productRef = productsRef.current[id];
    if (!deepEqual(product, productRef)) {
      productsRef.current[id] = product;
    }
    return productsRef.current[id];
  }, [products]);
  const getPrimaryProduct = useCallback(() => {
    return products.length > 0 ? products[0] : undefined;
  }, [products]);
  return /*#__PURE__*/React.createElement(ProductContext.Provider, {
    value: {
      getProduct,
      getActiveProduct,
      getPrimaryProduct,
      addProduct,
      updateProduct,
      removeProduct,
      showProduct,
      products
    }
  }, children);
};
export const useProductContext = () => useContext(ProductContext);
export const withProductContext = WrappedComponent => {
  return props => /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, useProductContext()));
};